/**
 * Who in the pool took each side, and what the week's results do to the
 * standings. Reads `/api/v1/pool_crowd` (Pool::WeekCrowd): every player's
 * picks for the week as abbreviations, beside their record before it.
 *
 * Everything is graded here, against the week's games the Picks page already
 * polls, so the standings move with the live scores:
 *
 *   * A pick is graded the way the pool grades it: against the frozen line,
 *     one point for a cover, a push is a tie (`coverMargin`).
 *   * The standings order is the pool's: wins (a 5-0 week's +2 bonus folded
 *     in), then fewest losses, then fewest ties. Everyone on one record is
 *     tied, and ranks at the group's first place (PoolStanding.tied_ranks).
 *   * The projection plays out every game not yet final a few thousand times
 *     -- live games at their live chance to cover, games to come at the
 *     model's pre-game chance -- and reports the middle of where you land.
 *
 * Nothing here ranks a pick or touches the board: who else picked a side does
 * not change its chance of covering (CLAUDE.md, "rank on winProbability").
 */
import { coverMargin, gameRow, resultFromMargin } from "./weekGames.js";
import { gameState, sideLiveCover } from "./gameday.js";

// The +2 for a perfect week. The Automatic is never paid it.
export const PERFECT_WEEK_BONUS = 2;
export const PICKS_PER_WEEK = 5;
// "Closest to you": players this many wins either side of you before the week.
export const NEAR_WINS = 2;
export const PROJECTION_RUNS = 2000;
export const SWING_RUNS = 600;

const FLIP = { win: "loss", loss: "win", push: "push" };

/**
 * One entry per game with a frozen line: its two abbreviations, its state, its
 * graded result once final, and the chance of each side covering (and of a
 * push) while it is not.
 */
export function gameOutcomes(games) {
  return (games || []).filter((game) => game.league_spread != null).map((game) => {
    const state = gameState(game);
    const base = { id: game.id, home: game.home_abbr, away: game.away_abbr, state };
    if (state === "final") {
      const home = resultFromMargin(coverMargin(game, "home"));
      return { ...base, result: home ? { home, away: FLIP[home] } : null, p: null };
    }
    return { ...base, result: null, p: coverChances(game, state) };
  });
}

function coverChances(game, state) {
  let home = null;
  let away = null;
  if (state === "live") {
    home = sideLiveCover(game, "home")?.p ?? null;
    away = sideLiveCover(game, "away")?.p ?? null;
  } else {
    const h = gameRow(game, "home").winProbability;
    const a = gameRow(game, "away").winProbability;
    home = h == null ? null : h / 100;
    away = a == null ? null : a / 100;
  }
  if (home == null || away == null) return { home: 0.5, away: 0.5, push: 0 };
  const total = home + away;
  if (total > 1) return { home: home / total, away: away / total, push: 0 };
  return { home, away, push: 1 - total };
}

/** {abbr: "win" | "loss" | "push"} for every final game, both sides. */
export function finalResults(outcomes, { except = null } = {}) {
  const results = {};
  for (const outcome of outcomes) {
    if (!outcome.result || outcome.id === except) continue;
    results[outcome.home] = outcome.result.home;
    results[outcome.away] = outcome.result.away;
  }
  return results;
}

/** How many players took each side of one game, and how many skipped it. */
export function gameCrowd(players, outcome, ids = null) {
  const pool = ids ? players.filter((p) => ids.has(p.id)) : players;
  const home = pool.filter((p) => p.picks.includes(outcome.home)).length;
  const away = pool.filter((p) => p.picks.includes(outcome.away)).length;
  return { home, away, picked: home + away, skipped: pool.length - home - away, field: pool.length };
}

/**
 * A player's week so far against `results`: wins, losses, ties, and the picks
 * still open. The bonus lands only on a full five, all won.
 */
export function weekRecord(player, results) {
  let wins = 0;
  let losses = 0;
  let ties = 0;
  for (const abbr of player.picks) {
    const result = results[abbr];
    if (result === "win") wins += 1;
    else if (result === "loss") losses += 1;
    else if (result === "push") ties += 1;
  }
  const open = player.picks.length - wins - losses - ties;
  const bonus = !player.is_automatic && wins === PICKS_PER_WEEK && player.picks.length === PICKS_PER_WEEK
    ? PERFECT_WEEK_BONUS
    : 0;
  return { wins: wins + bonus, losses, ties, open };
}

/**
 * {id: {rank, tied, wins, losses, ties}} for the whole field, the pool's
 * order: more wins, then fewer losses, then fewer ties. Ranks are shared by
 * everyone on one record, at the group's first place.
 */
export function rankField(players, results) {
  const records = players.map((player) => {
    const week = weekRecord(player, results);
    return {
      id: player.id,
      wins: player.wins + week.wins,
      losses: player.losses + week.losses,
      ties: player.ties + week.ties,
    };
  });
  return rankRecords(records);
}

function better(a, b) {
  return b.wins - a.wins || a.losses - b.losses || a.ties - b.ties;
}

function rankRecords(records) {
  const sorted = [...records].sort(better);
  const ranks = {};
  let groupStart = 0;
  for (let i = 0; i < sorted.length; i += 1) {
    if (i > 0 && better(sorted[i - 1], sorted[i]) !== 0) groupStart = i;
    ranks[sorted[i].id] = { ...sorted[i], rank: groupStart + 1 };
  }
  // A tie needs a second player on the same record.
  const counts = {};
  for (const r of records) counts[ranks[r.id].rank] = (counts[ranks[r.id].rank] || 0) + 1;
  for (const id of Object.keys(ranks)) ranks[id].tied = counts[ranks[id].rank] > 1;
  return ranks;
}

/** "T14", or "14" for a rank nobody shares. */
export function rankLabel(entry) {
  if (!entry || entry.rank == null) return "—";
  return `${entry.tied ? "T" : ""}${entry.rank}`;
}

/**
 * The rank and record a live row started the week on, or "—" / "" without a
 * baseline: in Week 1 everyone's 0-0-0 start ranks as a shared #1, which is
 * no place anybody held.
 */
export function baselineRank(row) {
  return row?.hasBaseline === false ? "—" : rankLabel(row?.before);
}

export function baselineRecord(row) {
  return row?.hasBaseline === false || !row?.before ? "" : recordLabel(row.before);
}

/** Ids of the players within NEAR_WINS wins of `meId` before the week, not you. */
export function closestTo(players, meId) {
  const me = players.find((p) => p.id === meId);
  if (!me) return new Set();
  return new Set(
    players.filter((p) => p.id !== meId && Math.abs(p.wins - me.wins) <= NEAR_WINS).map((p) => p.id)
  );
}

// Seeded, so a projection does not shuffle on every re-render.
function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function sample(p, rand) {
  const roll = rand();
  if (roll < p.home) return "home";
  if (roll < p.home + p.away) return "away";
  return "push";
}

/**
 * Where each player finishes once every open game is played out, `runs`
 * times. `fixed` pins one game's result ({id, side: "home" | "away"}) for the
 * swing tiles. Returns {id: {median, low, high}} -- the middle finish and the
 * 10th-90th percentile range -- for `ids` (default: everyone).
 */
export function projectField(players, outcomes, { runs = PROJECTION_RUNS, seed = 7, fixed = null, ids = null } = {}) {
  const base = finalResults(outcomes);
  const open = outcomes.filter((o) => !o.result && o.p);
  const wanted = ids ? [...ids] : players.map((p) => p.id);
  const finishes = Object.fromEntries(wanted.map((id) => [id, []]));
  const rand = mulberry32(seed);

  for (let run = 0; run < runs; run += 1) {
    const results = { ...base };
    for (const outcome of open) {
      // Always draw, even for a pinned game: the swing tiles compare runs that
      // differ only in that game, so every other game must see the same rolls.
      const drawn = sample(outcome.p, rand);
      const side = fixed && fixed.id === outcome.id ? fixed.side : drawn;
      if (side === "push") {
        results[outcome.home] = "push";
        results[outcome.away] = "push";
      } else {
        results[outcome[side]] = "win";
        results[outcome[side === "home" ? "away" : "home"]] = "loss";
      }
    }
    const ranks = rankField(players, results);
    for (const id of wanted) finishes[id].push(ranks[id].rank);
  }

  const projected = {};
  for (const id of wanted) {
    const sorted = finishes[id].sort((a, b) => a - b);
    const at = (q) => sorted[Math.min(sorted.length - 1, Math.floor(q * sorted.length))];
    projected[id] = { median: at(0.5), low: at(0.1), high: at(0.9) };
  }
  return projected;
}

/**
 * Everything one Picks row's pool section shows, for the signed-in picker:
 * the whole pool's split, the closest players' split, and either what each
 * result would do to your rank (open game) or what it did (final).
 */
export function gamePoolView(crowd, outcomes, gameId) {
  if (!crowd?.available) return { available: false };
  const outcome = outcomes.find((o) => o.id === gameId);
  if (!outcome) return { available: false };

  const players = crowd.players;
  const meId = crowd.me?.identified ? crowd.me.id : null;
  const near = meId ? closestTo(players, meId) : new Set();
  const view = {
    available: true,
    home: outcome.home,
    away: outcome.away,
    result: outcome.result,
    pool: gameCrowd(players, outcome),
    near: meId ? gameCrowd(players, outcome, near) : null,
    mine: meId ? sideOf(players.find((p) => p.id === meId), outcome) : null,
    swing: null,
    moved: null,
  };
  if (!meId) return view;

  if (outcome.result) {
    const before = rankField(players, finalResults(outcomes, { except: gameId }))[meId].rank;
    const after = rankField(players, finalResults(outcomes))[meId].rank;
    view.moved = before - after;
  } else {
    // Arrows are against your rank right now, the number the standing card
    // prints beside them; "you to N" is the projected finish with this game
    // pinned and everything else still played out.
    const now = rankField(players, finalResults(outcomes))[meId].rank;
    const swing = (side) => {
      const rank = projectField(players, outcomes, { runs: SWING_RUNS, fixed: { id: gameId, side }, ids: [meId] })[meId].median;
      return { rank, delta: now - rank };
    };
    view.swing = { home: swing("home"), away: swing("away") };
  }
  return view;
}

function sideOf(player, outcome) {
  if (!player) return null;
  if (player.picks.includes(outcome.home)) return "home";
  if (player.picks.includes(outcome.away)) return "away";
  return null;
}

/**
 * The standing card and the Pool tab's live table: each player's record and
 * rank now (finals only), their week so far, whether they still have a pick in
 * a live game, and their projected finish.
 */
export function liveStandings(crowd, outcomes) {
  if (!crowd?.available) return null;
  const players = crowd.players;
  const results = finalResults(outcomes);
  const now = rankField(players, results);
  const before = rankField(players, {});
  const projected = projectField(players, outcomes);
  const live = new Set(outcomes.filter((o) => o.state === "live").flatMap((o) => [o.home, o.away]));
  const open = outcomes.filter((o) => !o.result).length;

  const rows = players.map((player) => {
    const week = weekRecord(player, results);
    return {
      id: player.id,
      name: player.name,
      isMe: player.is_me,
      isAutomatic: player.is_automatic,
      before: before[player.id],
      // Whether `before` is a rank they held. Payloads predating the flag count as held.
      hasBaseline: player.ranked !== false,
      now: now[player.id],
      week,
      inLive: player.picks.some((abbr) => live.has(abbr) && !(abbr in results)),
      projected: projected[player.id],
    };
  }).sort((a, b) => a.now.rank - b.now.rank || a.name.localeCompare(b.name));

  return {
    rows,
    me: rows.find((row) => row.isMe) || null,
    openGames: open,
    liveGames: outcomes.filter((o) => o.state === "live").length,
    fieldSize: players.length,
  };
}

/**
 * Places a live row is set to move this week, against the confirmed standings
 * it started from (`before`), not its mid-week rank: positive is up. While
 * games are open that is the projected median; once all are final, `now`.
 * Null when there is no baseline to compare with: Week 1, or a player new to
 * the sheet, whose 0-0-0 start would read as a fall or an unearned climb.
 */
export function projectedMove(row, openGames) {
  if (row?.hasBaseline === false) return null;
  const start = row?.before?.rank;
  const end = openGames ? row?.projected?.median : row?.now?.rank;
  if (start == null || end == null) return null;
  return start - end;
}

const LIVE_AROUND = 4;
const LIVE_TOP = 10;

/**
 * The tab the live table opens on: Following while there is anyone to show
 * there (you, or a player you follow), else Top.
 */
export function defaultLiveView(table, followedNames = []) {
  if (!table) return "top";
  const followed = new Set(followedNames);
  // The Standings list always carries the Automatic, so it alone is nobody.
  return table.me || table.rows.some((row) => !row.isAutomatic && followed.has(row.name)) ? "following" : "top";
}

/**
 * The rows one live-table tab shows. `following` is you plus the players the
 * Standings table follows, matched by name -- the live table's ids are the
 * crowd payload's, not the standings rows'. A followed name missing from the
 * crowd is skipped.
 */
export function liveViewRows(table, view, followedNames = []) {
  const rows = table.rows;
  if (view === "following") {
    const followed = new Set(followedNames);
    return rows.filter((row) => row.isMe || followed.has(row.name));
  }
  if (view === "around" && table.me) {
    const mine = rows.findIndex((row) => row.isMe);
    const start = Math.max(0, Math.min(mine - LIVE_AROUND, rows.length - (LIVE_AROUND * 2 + 1)));
    return rows.slice(start, start + LIVE_AROUND * 2 + 1);
  }
  return rows.slice(0, LIVE_TOP);
}

/** "28-17", or "28-17-1" with ties. */
export function recordLabel({ wins, losses, ties }) {
  return ties ? `${wins}–${losses}–${ties}` : `${wins}–${losses}`;
}
